import React, { useState, useEffect, useRef } from 'react';

interface AnimatedScoreCircleProps {
  score: number | null;
  size?: number;
  strokeWidth?: number;
  animationDuration?: number;
  isLoading?: boolean;
}

const AnimatedScoreCircle: React.FC<AnimatedScoreCircleProps> = ({
  score,
  size = 70,
  strokeWidth = 4,
  animationDuration = 800, // 800ms like Grammarly
  isLoading = false
}) => {
  const [animatedScore, setAnimatedScore] = useState<number>(score ?? 0);
  const [previousScore, setPreviousScore] = useState<number>(score ?? 0);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>();
  const firstRender = useRef(true);

  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Smooth animation function
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  // Get dynamic gradient colors based on score (like Dashboard!)
  const getScoreGradient = (currentScore: number) => {
    if (currentScore >= 80) {
      // Excellent - Bright green gradient
      return {
        id: 'excellent-gradient',
        startColor: '#10B981', // Emerald green
        endColor: '#059669',   // Darker emerald
        glowColor: '#10B981'
      };
    }
    if (currentScore >= 70) {
      // Good - Green to lime gradient  
      return {
        id: 'good-gradient',
        startColor: '#43CC48', // Like Dashboard green!
        endColor: '#216624',   // Like Dashboard darker green!
        glowColor: '#43CC48'
      };
    }
    if (currentScore >= 60) {
      // Moderate - Yellow to orange gradient
      return {
        id: 'moderate-gradient',
        startColor: '#FBBF24', // Yellow
        endColor: '#F59E0B',   // Orange
        glowColor: '#FBBF24'
      };
    }
    if (currentScore >= 40) {
      // Weak - Orange to red gradient
      return {
        id: 'weak-gradient',
        startColor: '#FB923C', // Orange
        endColor: '#EF4444',   // Red
        glowColor: '#FB923C'
      };
    }
    // Poor - Red gradient
    return {
      id: 'poor-gradient',
      startColor: '#EF4444', // Red
      endColor: '#DC2626',   // Darker red
      glowColor: '#EF4444'
    };
  };

  // Animate score changes with instant color updates and NaN protection
  useEffect(() => {
    // Protect against NaN values
    const safeScore = (score === null || isNaN(score as number)) ? null : score;
    
    if (safeScore === null || isLoading) {
      setAnimatedScore(0);
      return;
    }

    // Additional NaN check for animatedScore
    const safeAnimatedScore = isNaN(animatedScore) ? 0 : animatedScore;
    
    // Skip animation if this is the first render (component just mounted)
    if (firstRender.current) {
      firstRender.current = false;
      setAnimatedScore(safeScore);
      setPreviousScore(safeScore);
      return;
    }

    if (safeScore === safeAnimatedScore) return;

    setPreviousScore(safeAnimatedScore);
    startTimeRef.current = performance.now();

    const animate = (currentTime: number) => {
      if (!startTimeRef.current || safeScore === null) return;

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeOutCubic(progress);

      const current = previousScore + (safeScore - previousScore) * easedProgress;
      setAnimatedScore(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setAnimatedScore(safeScore);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [score, animationDuration, isLoading]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Additional NaN protection for display
  const safeDisplayScore = (score === null || isNaN(score as number)) ? null : score;
  const safeAnimatedScore = isNaN(animatedScore) ? 0 : animatedScore;
  
  if (isLoading || safeDisplayScore === null) {
    return (
      <div className="loading-circle" style={{ width: size, height: size, position: 'relative' }}>
        <svg width={size} height={size}>
          <defs>
            {/* Loading gradient */}
            <linearGradient id="loading-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#E5E7EB" />
              <stop offset="50%" stopColor="#D1D5DB" />
              <stop offset="100%" stopColor="#E5E7EB" />
            </linearGradient>
          </defs>
          <circle
            stroke="url(#loading-gradient)"
            fill="transparent"
            strokeWidth={strokeWidth}
            r={radius}
            cx={center}
            cy={center}
            style={{
              animation: 'pulse 2s infinite',
              opacity: 0.7
            }}
          />
        </svg>
        <div className="loading-dots" style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}>
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
        </div>
      </div>
    );
  }

  const strokeDashoffset = circumference - (safeAnimatedScore / 100) * circumference;
  // Use actual score for instant color changes, animated score for circle progress
  const currentGradient = getScoreGradient(safeDisplayScore || safeAnimatedScore);

  return (
    <div className="animated-score-circle" style={{ 
      width: size, 
      height: size,
      position: 'relative'
    }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          {/* Dynamic gradient definitions */}
          <linearGradient id={currentGradient.id} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={currentGradient.startColor} />
            <stop offset="100%" stopColor={currentGradient.endColor} />
          </linearGradient>
          
          {/* Subtle inner shadow for depth */}
          <filter id="inner-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/> 
            </feMerge>
          </filter>
        </defs>
        
        {/* Background circle - light grey like Dashboard */}
        <circle
          stroke="#F3F4F6"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={radius}
          cx={center}
          cy={center}
        />
        
        {/* Animated progress circle with gradient */}
        <circle
          stroke={`url(#${currentGradient.id})`}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          r={radius}
          cx={center}
          cy={center}
          style={{
            transition: firstRender.current ? 'none' : `stroke-dashoffset ${animationDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            filter: `drop-shadow(0 0 8px ${currentGradient.glowColor}40)`
          }}
        />
      </svg>
      
      {/* Score text with dynamic color */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: size > 60 ? '26px' : '20px',
          fontWeight: '900',
          color: currentGradient.startColor,
          textAlign: 'center',
          lineHeight: 1,
          textShadow: `0 1px 3px ${currentGradient.glowColor}30`,
          transition: `color ${animationDuration * 0.2}ms ease-out`,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        {Math.round(safeAnimatedScore)}
      </div>
    </div>
  );
};

export default AnimatedScoreCircle; 